/**
 * GLB export: canonical clips → baked, rotation-only three.js tracks → a validated `.glb`.
 *
 * Design forced by digests/r-gltf-export.md (three r185):
 *  - Every bone must be a descendant of the exported root and `onlyVisible` must be `false`,
 *    otherwise `processSkin` writes `undefined` into `skins[].joints`.
 *  - Track names are `"<boneName>.quaternion"`. `PropertyBinding.parseTrackName` treats `:` and
 *    `/` as directory separators and `[ ] .` as syntax, so an unsafe bone name silently drops the
 *    track (or aborts the whole export). We refuse to export such a name instead.
 *  - `InterpolateSmooth` silently becomes LINEAR on export, so every clip is baked to one sample
 *    per integer frame and written as LINEAR (plan D9, D26).
 *  - The rotation-only invariant (plan D8) has ONE deliberate exception: the vertical Hips offset
 *    channel (`RotationKeyframe.hipsY`). A crouch cannot be expressed as a rotation, so a clip
 *    that actually leaves bind height bakes exactly one extra track — `<Hips>.position` — and the
 *    re-loaded buffer is asserted to contain no OTHER position track and no scale track at all
 *    (`GlbInspection.hasPositionOrScaleTracks`). Horizontal travel is still never written: the
 *    values come from `rig.hipsLocalPosition`, which only moves along the bind up axis. Root
 *    belongs to the game, Hips belongs to the clip.
 */

import {
  AnimationClip,
  InterpolateLinear,
  QuaternionKeyframeTrack,
  VectorKeyframeTrack,
} from 'three';
import type { KeyframeTrack, Material, Mesh, Object3D } from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';

import { AppError, MATERIAL_KEY, sampleClipForBake } from '../shared';
import type { CanonicalAnimationClip, CanonicalBoneId, QuaternionTuple, Vec3Tuple } from '../shared';
import { isTrackSafeName } from './glbImport';
import { inspectGlb } from './glbInspect';

/**
 * The minimum a poseable rig must offer for export. The runtime `BoundRig`
 * (`src/runtime/boundRig.ts`, plan D18) satisfies it; tests use a hand-written stand-in.
 */
export interface RigDriver {
  /** The source `Object3D` a canonical bone drives, or `undefined` when unmapped. */
  boneObject(id: CanonicalBoneId): Object3D | undefined;
  /** Canonical rotation → the source bone's local rotation (plan D5 rest-space correction). */
  toSourceLocal(id: CanonicalBoneId, q: QuaternionTuple): QuaternionTuple;
  /** Canonical bones this rig actually drives. Unmapped bones (often `Root`) are absent. */
  mappedBoneIds(): CanonicalBoneId[];
  /**
   * Hips local position for a normalized `hipsY`, or `null` when the channel is disabled on this
   * rig (Hips or a Foot unmapped, degenerate leg length). Required on `BoundRig`, OPTIONAL here:
   * the hand-written drivers in the tests predate the channel and a driver without it simply
   * exports no position track, which is the correct rotation-only behaviour.
   */
  hipsLocalPosition?(hipsY: number): Vec3Tuple | null;
}

export interface GlbExportMetadata {
  rigVersion: string;
  canonicalBoneMap: Partial<Record<CanonicalBoneId, string>>;
  worldCorrection: QuaternionTuple;
  sourceRigVersion?: string;
}

export interface ExportGlbArgs {
  root: Object3D;
  clips: CanonicalAnimationClip[];
  rig: RigDriver;
  metadata: GlbExportMetadata;
  /** Called just before `GLTFExporter` runs — put the rig back into its rest pose here. */
  beforeExport?: () => void;
  /** Called after the exporter finished, successfully or not. */
  afterExport?: () => void;
  /**
   * Write the mesh, the skeleton and the UVs but NO texture images.
   *
   * The character then loads as a fraction of its usual size and appears untextured; the game
   * fetches the image files in parallel and calls `applyTextures` when they arrive. Geometry is
   * untouched — every UV set is written exactly as in a normal export — so nothing has to be
   * re-derived when the textures land.
   *
   * The validation gets stricter, not looser: the re-loaded buffer must contain zero images AND
   * the same number of UV-bearing meshes as the live model had.
   */
  stripTextures?: boolean;
  /**
   * Source glTF material index per live material, from the import (`ImportedModel.materialSource`).
   *
   * Only read when `stripTextures` is set: it becomes `userData[MATERIAL_KEY]` on the stripped
   * clone, which the exporter writes into glTF `extras` and `applyTextures` reads back to know
   * which manifest entry belongs to which material. Optional — without it the manifest still
   * matches by material NAME, which is what a hand-built driver or a tool-built material gets.
   */
  materialSource?: ReadonlyMap<Material, number>;
}

/** Key written into glTF `extras` (plan D19); the runtime SDK reads it back to rebuild a binding. */
/** Frozen at the pre-rename spelling: already-exported GLBs carry this key and must keep loading. */
export const GLB_METADATA_KEY = 'animstatex';

// ---------------------------------------------------------------------------
// Baking
// ---------------------------------------------------------------------------

/**
 * Bake a canonical clip into a three.js `AnimationClip` of rotation-only, LINEAR tracks expressed
 * in the target rig's source-local space.
 *
 * One sample per integer frame (`sampleClipForBake`, plan D9) so any easing — including `step`,
 * which becomes a one-frame ramp (plan D26) — reproduces the editor's playback.
 *
 * A clip whose `hipsY` channel actually moves the pelvis gets ONE extra track, `<Hips>.position`,
 * baked from the same samples through `rig.hipsLocalPosition`. A clip without the channel — or
 * with an all-zero one, which is what keying the pelvis at bind height produces — is untouched:
 * the output is byte-for-byte what it was before the channel existed.
 *
 * @throws AppError `GLB_EXPORT_FAILED` when a mapped bone's name cannot be used in a track name.
 */
export function clipToThreeAnimation(
  clip: CanonicalAnimationClip,
  rig: RigDriver,
): AnimationClip {
  const samples = sampleClipForBake(clip);
  const times = samples.times;
  const frameCount = times.length;

  const tracks: KeyframeTrack[] = [];
  const usedNames = new Set<string>();
  const scratch: QuaternionTuple = [0, 0, 0, 1];

  for (const id of rig.mappedBoneIds()) {
    const rotations = samples.rotations[id];
    if (!rotations) continue; // bone is mapped but this clip does not animate it

    const bone = rig.boneObject(id);
    if (!bone) continue; // mapped but not resolvable on this model

    const name = `${bone.name}.quaternion`;
    if (!isTrackSafeName(bone.name)) {
      throw new AppError(
        'GLB_EXPORT_FAILED',
        `Bone "${bone.name}" (canonical ${id}) has a name that cannot be used in a glTF ` +
          'animation track. Re-import the model so bone names are sanitized.',
        { boneId: id, boneName: bone.name },
      );
    }
    if (usedNames.has(name)) {
      throw new AppError(
        'GLB_EXPORT_FAILED',
        `Two canonical bones resolve to the same source bone "${bone.name}"; the exported ` +
          'animation would be ambiguous.',
        { boneId: id, boneName: bone.name },
      );
    }
    usedNames.add(name);

    const values = new Float32Array(frameCount * 4);
    for (let f = 0; f < frameCount; f++) {
      scratch[0] = rotations[f * 4 + 0];
      scratch[1] = rotations[f * 4 + 1];
      scratch[2] = rotations[f * 4 + 2];
      scratch[3] = rotations[f * 4 + 3];
      const local = rig.toSourceLocal(id, scratch);
      values[f * 4 + 0] = local[0];
      values[f * 4 + 1] = local[1];
      values[f * 4 + 2] = local[2];
      values[f * 4 + 3] = local[3];
    }

    tracks.push(new QuaternionKeyframeTrack(name, times, values, InterpolateLinear));
  }

  appendHipsPositionTrack(samples.hipsY, times, frameCount, rig, tracks, usedNames);

  const duration = clip.fps > 0 ? clip.durationFrames / clip.fps : 0;
  return new AnimationClip(clip.name, duration, tracks);
}

/**
 * Largest sampled `|hipsY|` that still counts as "the pelvis never left bind height".
 *
 * Not a noise floor and unrelated to `HIPS_Y_IMPORT_THRESHOLD` (`src/rig/clipSampler.ts`, 1e-3,
 * which decides whether an IMPORTED motion is worth keeping): the values compared here were
 * authored in the editor, so this only has to separate an exact zero from a real offset, and a
 * tolerance rather than `!== 0` guards against a float that survived a round trip as 1e-17.
 */
const HIPS_Y_BAKE_THRESHOLD = 1e-6;

/**
 * The one position track this exporter is allowed to write: the vertical Hips offset.
 *
 * Every condition below is a reason to write nothing and stay rotation-only — the clip does not
 * author the channel, every sample is bind height, the driver predates the channel, the rig has it
 * disabled (`hipsLocalPosition(0)` answers `null`), or the Hips is not resolvable on this model.
 * "Write nothing" is always the safe answer: the offsets are normalized, so a consumer that never
 * sees the track gets the character standing at bind height rather than sunk into the floor.
 *
 * The all-zero case is the common one, not an edge case. Keying the Hips in the editor always
 * writes `hipsY`, zero included — it has to, because the channel is SPARSE and an omitted 0 is
 * "this key says nothing", which would let an earlier crouch bleed forward across it. So every
 * clip keyed on the pelvis carries the channel, and most of them carry nothing but zeros: the
 * author posed the hips rotation and left the height alone. Baking a constant rest-position track
 * for those would add bytes to every such GLB and put a false "Hips vertical offset" line in front
 * of an author who never authored one. The clip data stays truthful either way — this is a bake
 * decision, not a data one, and `clipHasHipsY` still answers `true`.
 */
function appendHipsPositionTrack(
  offsets: Float32Array | undefined,
  times: Float32Array,
  frameCount: number,
  rig: RigDriver,
  tracks: KeyframeTrack[],
  usedNames: Set<string>,
): void {
  if (!offsets || !rig.hipsLocalPosition) return;

  let peak = 0;
  for (let f = 0; f < offsets.length; f++) {
    const magnitude = Math.abs(offsets[f]);
    if (magnitude > peak) peak = magnitude;
  }
  if (peak <= HIPS_Y_BAKE_THRESHOLD) return;

  const rest = rig.hipsLocalPosition(0);
  if (!rest) return;

  const bone = rig.boneObject('Hips');
  if (!bone) return;

  const name = `${bone.name}.position`;
  // Same two guards as the rotation tracks, for the same reason: `PropertyBinding` silently drops
  // a track whose node name it cannot parse, and a duplicate name binds to whichever node the
  // exporter's DFS reaches first (`.claude/vault/glb-export-bone-names.md`).
  if (!isTrackSafeName(bone.name)) {
    throw new AppError(
      'GLB_EXPORT_FAILED',
      `Bone "${bone.name}" (canonical Hips) has a name that cannot be used in a glTF ` +
        'animation track. Re-import the model so bone names are sanitized.',
      { boneId: 'Hips', boneName: bone.name },
    );
  }
  if (usedNames.has(name)) {
    throw new AppError(
      'GLB_EXPORT_FAILED',
      `Two canonical bones resolve to the same source bone "${bone.name}"; the exported ` +
        'animation would be ambiguous.',
      { boneId: 'Hips', boneName: bone.name },
    );
  }
  usedNames.add(name);

  const values = new Float32Array(frameCount * 3);
  for (let f = 0; f < frameCount; f++) {
    // `hipsLocalPosition(0)` already answered non-null, so the channel is enabled and every
    // sample resolves; `rest` keeps the track well-formed if a hand-written driver disagrees.
    const p = rig.hipsLocalPosition(offsets[f]) ?? rest;
    values[f * 3 + 0] = p[0];
    values[f * 3 + 1] = p[1];
    values[f * 3 + 2] = p[2];
  }

  tracks.push(new VectorKeyframeTrack(name, times, values, InterpolateLinear));
}

// ---------------------------------------------------------------------------
// Editor-only helpers
// ---------------------------------------------------------------------------

interface DetachedChild {
  parent: Object3D;
  child: Object3D;
  index: number;
}

/**
 * Temporarily lift out gizmos, joint markers and other viewport helpers. They are flagged
 * `userData.editorOnly === true`; hiding them is not an option because `onlyVisible` is `false`.
 */
function detachEditorOnly(root: Object3D): DetachedChild[] {
  const flagged: Object3D[] = [];
  root.traverse((o) => {
    if (o !== root && o.userData && o.userData.editorOnly === true) flagged.push(o);
  });

  const detached: DetachedChild[] = [];
  for (const child of flagged) {
    const parent = child.parent;
    if (!parent) continue;
    const index = parent.children.indexOf(child);
    if (index < 0) continue;
    parent.children.splice(index, 1);
    child.parent = null;
    detached.push({ parent, child, index });
  }
  return detached;
}

function reattachEditorOnly(detached: DetachedChild[]): void {
  // Reverse order so nested helpers land back in their original slots.
  for (let i = detached.length - 1; i >= 0; i--) {
    const { parent, child, index } = detached[i];
    child.parent = parent;
    parent.children.splice(Math.min(index, parent.children.length), 0, child);
  }
}

// ---------------------------------------------------------------------------
// Texture stripping
// ---------------------------------------------------------------------------

interface SwappedMaterial {
  mesh: Mesh;
  original: Material | Material[];
  replacement: Material | Material[];
}

/**
 * The texture-valued properties of a material, found by ENUMERATION rather than from a list.
 *
 * A list is the tempting version and it is wrong: the set of map properties differs per material
 * class and grows with every three release, and one slot missed here means one image survives in a
 * file that claims to have none — a silent lie, which is the exact failure this feature exists to
 * avoid. `disposeResources.ts` walks materials the same way and for the same reason.
 */
function textureProperties(material: Material): string[] {
  const record = material as unknown as Record<string, unknown>;
  const found: string[] = [];
  for (const [key, value] of Object.entries(record)) {
    if ((value as { isTexture?: boolean } | null)?.isTexture === true) found.push(key);
  }
  return found;
}

/** Does this material reference any texture at all? A material that does not is left alone. */
function hasTexture(material: Material): boolean {
  return textureProperties(material).length > 0;
}

/**
 * Scalar values that only make sense while a texture is present, and the matte placeholder each
 * one drops to when that texture is removed.
 *
 * `GLTFLoader` defaults `metalness` and `roughness` to **1.0** when the glTF omits the factors
 * (`GLTFLoader.js:3586-3587`) — which is precisely what an exporter writes when those values live
 * in the metallic-roughness texture. Strip the texture and keep the factor and the character loads
 * as a fully metallic, perfectly smooth BLACK MIRROR. An `emissiveFactor` masked by an
 * `emissiveTexture` does the same thing in the other direction: glowing eyes become a white-hot
 * silhouette. Either one turns the "show it now, texture it later" placeholder into something
 * worse than no character at all, and every structural check still passes.
 *
 * The real values are recorded in the texture manifest (`ManifestFactors`) and put back by
 * `applyTextures` together with the texture, so nothing is lost — it is deferred, like the image.
 */
const NEUTRALIZED_BY_SLOT: ReadonlyArray<{ map: string; property: string; placeholder: unknown }> = [
  { map: 'metalnessMap', property: 'metalness', placeholder: 0 },
  { map: 'roughnessMap', property: 'roughness', placeholder: 1 },
  { map: 'emissiveMap', property: 'emissive', placeholder: null },
];

/**
 * A copy of `material` with every texture reference dropped, the factors those textures were
 * masking neutralized, and everything else — name, base colour, transparency, side, alpha mode —
 * kept exactly as it was.
 *
 * Keeping each material rather than substituting one shared placeholder is the whole reason the
 * deferred attach can work: the manifest addresses materials, so each one has to survive the export
 * as a distinct, identifiable material sitting on the same meshes it always did.
 */
function stripMaterial(material: Material, sourceIndex: number | undefined): Material {
  const clone = material.clone();
  const record = clone as unknown as Record<string, unknown>;

  const present = new Set(textureProperties(clone));
  for (const property of present) record[property] = null;

  for (const rule of NEUTRALIZED_BY_SLOT) {
    if (!present.has(rule.map) || !(rule.property in record)) continue;
    if (rule.placeholder === null) {
      // `emissive` is a `Color`; assigning a number would replace the object the renderer reads.
      (record[rule.property] as { setRGB(r: number, g: number, b: number): void }).setRGB(0, 0, 0);
    } else {
      record[rule.property] = rule.placeholder;
    }
  }

  // Written onto the CLONE only, so a normal textured export stays byte-for-byte what it was.
  // `GLTFExporter.serializeUserData` turns it into the material's glTF `extras`.
  if (sourceIndex !== undefined) {
    clone.userData = { ...clone.userData, [MATERIAL_KEY]: sourceIndex };
  }
  return clone;
}

/**
 * Swap every textured material under `root` for a texture-free copy, for the duration of an export.
 *
 * The clones are thrown away and DISPOSED by `restoreMaterials`; the originals are never touched.
 * Nulling the maps on the LIVE materials and putting them back would be less code and a worse
 * idea: the viewport shares those exact instances, so an export that threw halfway would hand the
 * user back a permanently white character with no way to know why. (A repaint during the export
 * still shows the untextured clones — `USE_MAP` is part of the program cache key, so a map-less
 * clone compiles its own program either way. That is a transient flicker; the originals coming
 * back intact is the part that matters.)
 */
function stripTexturesFromMaterials(
  root: Object3D,
  materialSource: ReadonlyMap<Material, number> | undefined,
): SwappedMaterial[] {
  const swapped: SwappedMaterial[] = [];
  /** One clone per material, so a material shared by several meshes stays shared. */
  const clones = new Map<Material, Material>();

  const cloneOf = (material: Material): Material => {
    let clone = clones.get(material);
    if (!clone) {
      clone = stripMaterial(material, materialSource?.get(material));
      clones.set(material, clone);
    }
    return clone;
  };

  root.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh !== true || !mesh.material) return;

    if (Array.isArray(mesh.material)) {
      if (!mesh.material.some(hasTexture)) return;
      const original = mesh.material;
      const replacement = original.map(cloneOf);
      mesh.material = replacement;
      swapped.push({ mesh, original, replacement });
      return;
    }

    if (!hasTexture(mesh.material)) return;
    const original = mesh.material;
    mesh.material = cloneOf(original);
    swapped.push({ mesh, original, replacement: mesh.material });
  });

  return swapped;
}

function restoreMaterials(swapped: SwappedMaterial[]): void {
  const disposed = new Set<Material>();
  for (const entry of swapped) {
    entry.mesh.material = entry.original;
    for (const clone of Array.isArray(entry.replacement) ? entry.replacement : [entry.replacement]) {
      // The clones never reached a renderer, but `disposeResources` owns material lifetimes in
      // this app and a clone that outlives its export would be a material nothing can free.
      if (!disposed.has(clone)) {
        disposed.add(clone);
        clone.dispose();
      }
    }
  }
}

/** Meshes whose geometry carries a `uv` attribute — the "before" half of the UV survival check. */
function countUvMeshes(root: Object3D): number {
  let count = 0;
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh === true && mesh.geometry?.getAttribute('uv')) count++;
  });
  return count;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Export `root` with the given canonical clips baked through `rig`, embed the canonical-rig
 * metadata in glTF `extras`, then load the result back and verify it.
 *
 * @throws AppError `GLB_EXPORT_FAILED` (the exporter refused) or `GLB_EXPORT_INVALID`
 *         (the produced buffer does not re-load with the expected skeleton and tracks).
 */
export async function exportGlb(args: ExportGlbArgs): Promise<ArrayBuffer> {
  const { root, clips, rig, metadata, beforeExport, afterExport } = args;
  const stripTextures = args.stripTextures === true;

  // Counted BEFORE anything is swapped or detached, so it is a fact about the model the user has
  // rather than about the graph the exporter was handed.
  const uvMeshesBefore = countUvMeshes(root);

  const animations = clips.map((clip) => clipToThreeAnimation(clip, rig));

  // Snapshot mapped bone rotations so an aborted export never leaves the editor posed differently.
  const posed: Array<{ object: Object3D; x: number; y: number; z: number; w: number }> = [];
  for (const id of rig.mappedBoneIds()) {
    const bone = rig.boneObject(id);
    if (!bone) continue;
    posed.push({
      object: bone,
      x: bone.quaternion.x,
      y: bone.quaternion.y,
      z: bone.quaternion.z,
      w: bone.quaternion.w,
    });
  }

  // The Hips is the one bone whose POSITION this export can disturb: `beforeExport` puts the rig
  // back to rest, and resetting to rest now moves the hips to its bind height as well as its bind
  // rotation. Snapshotting rotations alone would hand the editor back a character still crouched
  // (or, worse, standing straight) after an export that failed halfway through.
  const hipsBone = rig.boneObject('Hips');
  const hipsPosition = hipsBone
    ? { object: hipsBone, x: hipsBone.position.x, y: hipsBone.position.y, z: hipsBone.position.z }
    : null;

  const hadMetadata = Object.prototype.hasOwnProperty.call(root.userData, GLB_METADATA_KEY);
  const previousMetadata: unknown = root.userData[GLB_METADATA_KEY];

  let detached: DetachedChild[] = [];
  let swappedMaterials: SwappedMaterial[] = [];
  let buffer: ArrayBuffer;

  try {
    beforeExport?.();
    detached = detachEditorOnly(root);
    if (stripTextures) swappedMaterials = stripTexturesFromMaterials(root, args.materialSource);
    root.userData[GLB_METADATA_KEY] = metadata;
    root.updateMatrixWorld(true);

    let result: ArrayBuffer | { [key: string]: unknown };
    try {
      /*
       * `trs: true` is passed explicitly rather than left to the exporter. three r185 sets it
       * itself only when `animations.length > 0`; a rig-only export (zero animations) therefore
       * wrote 22 of 24 nodes as a single `matrix` instead of `translation/rotation/scale`.
       * GLTFLoader decomposes those back correctly — the rest pose was measured equal within
       * 1e-4 — so this is about structural consistency for third-party importers, not about our
       * own round trip. A caller-supplied flag survives the exporter's own defaulting.
       */
      result = await new GLTFExporter().parseAsync(root, {
        binary: true,
        onlyVisible: false,
        animations,
        trs: true,
      });
    } catch (error) {
      throw new AppError(
        'GLB_EXPORT_FAILED',
        `The GLB could not be written: ${error instanceof Error ? error.message : String(error)}`,
        error,
      );
    }

    if (!(result instanceof ArrayBuffer)) {
      throw new AppError(
        'GLB_EXPORT_FAILED',
        'The exporter returned glTF JSON instead of a binary .glb buffer.',
      );
    }
    buffer = result;
  } finally {
    // Materials first: they were swapped last, and a mesh must be holding its own material again
    // before anything else looks at the graph.
    restoreMaterials(swappedMaterials);
    reattachEditorOnly(detached);
    if (hadMetadata) root.userData[GLB_METADATA_KEY] = previousMetadata;
    else delete root.userData[GLB_METADATA_KEY];
    for (const entry of posed) entry.object.quaternion.set(entry.x, entry.y, entry.z, entry.w);
    if (hipsPosition) {
      hipsPosition.object.position.set(hipsPosition.x, hipsPosition.y, hipsPosition.z);
    }
    root.updateMatrixWorld(true);
    afterExport?.();
  }

  await validateExportedGlb(buffer, clips, rig, stripTextures ? uvMeshesBefore : null);
  return buffer;
}

/**
 * Compare exported animation names against the selected ones as a MULTISET, not by count
 * (wave-5 review finding 9): equal counts say nothing about which clips actually made it, so an
 * export that silently dropped "Walk" and gained "Walk.001" used to validate clean. Duplicated
 * names are legal, so cardinality matters — hence the count map rather than two sets.
 */
export function diffClipNames(
  exported: readonly string[],
  selected: readonly string[],
): { missing: string[]; unexpected: string[] } {
  const remaining = new Map<string, number>();
  for (const name of exported) remaining.set(name, (remaining.get(name) ?? 0) + 1);

  const missing: string[] = [];
  for (const name of selected) {
    const left = remaining.get(name) ?? 0;
    if (left === 0) missing.push(name);
    else remaining.set(name, left - 1);
  }

  const unexpected = [...remaining.entries()].flatMap(([name, count]) =>
    Array.from({ length: count }, () => name),
  );
  return { missing, unexpected };
}

/**
 * @param expectUvMeshes when non-null, this was a texture-free export and the file must contain
 *        zero images and this many UV-bearing meshes. `null` for a normal export, which asserts
 *        neither: a textured GLB is supposed to have images.
 */
async function validateExportedGlb(
  buffer: ArrayBuffer,
  clips: CanonicalAnimationClip[],
  rig: RigDriver,
  expectUvMeshes: number | null,
): Promise<void> {
  let inspection;
  try {
    // Name the Hips node from the rig we are HOLDING rather than making the validator read our own
    // metadata back out of the buffer. Otherwise any future export path that drops or reshapes the
    // `animstatex` block would make this refuse our own legal Hips offset track as "the
    // rotation-only invariant is broken" — the safe direction, but self-inflicted and baffling.
    inspection = await inspectGlb(buffer, { hipsNodeName: rig.boneObject('Hips')?.name });
  } catch (error) {
    throw new AppError(
      'GLB_EXPORT_INVALID',
      `The exported GLB could not be loaded back: ${
        error instanceof Error ? error.message : String(error)
      }`,
      error,
    );
  }

  const brokenJoints = inspection.warnings.filter((w) => w.includes('.joints['));
  if (brokenJoints.length > 0) {
    throw new AppError(
      'GLB_EXPORT_INVALID',
      'The exported GLB has undefined skin joints — some skeleton bones were not part of the ' +
        'exported node graph.',
      { warnings: brokenJoints },
    );
  }

  const present = new Set(inspection.boneNames);
  const missing: string[] = [];
  for (const id of rig.mappedBoneIds()) {
    const bone = rig.boneObject(id);
    if (!bone) continue;
    if (!present.has(bone.name)) missing.push(`${id} -> ${bone.name}`);
  }
  if (missing.length > 0) {
    throw new AppError(
      'GLB_EXPORT_INVALID',
      `The exported GLB is missing ${missing.length} mapped bone(s) from its skin joints: ` +
        `${missing.join(', ')}.`,
      { missing, boneNames: inspection.boneNames },
    );
  }

  // `hasPositionOrScaleTracks` means "a scale track, or a position track on anything other than
  // the mapped Hips" — our own vertical-offset track is the one exception and is counted in
  // `hipsPositionTrackCount` instead. Everything else still fails the export.
  if (inspection.hasPositionOrScaleTracks) {
    throw new AppError(
      'GLB_EXPORT_INVALID',
      'The exported GLB contains position or scale animation tracks beyond the Hips vertical ' +
        'offset, which breaks the rotation-only invariant.',
      { clips: inspection.clips, hipsPositionTrackCount: inspection.hipsPositionTrackCount },
    );
  }

  /*
   * The texture-free export's two promises, checked against the file that was actually written
   * rather than against the intent that wrote it.
   *
   * The UV check is the one the user asked for by name: an export that shipped the mesh but lost
   * its UV map would look like a success — smaller file, no images, loads fine — and only fail
   * weeks later when the textures arrive and land on nothing. It compares against the count taken
   * off the LIVE model before the swap, so a model with no UVs at all (0 === 0) passes, and a
   * model that lost one does not.
   */
  if (expectUvMeshes !== null) {
    if (inspection.imageCount > 0) {
      throw new AppError(
        'GLB_EXPORT_INVALID',
        `A texture-free export still contains ${inspection.imageCount} embedded image(s). ` +
          'Some material kept a texture reference; the GLB was not written.',
        { imageCount: inspection.imageCount, materialNames: inspection.materialNames },
      );
    }
    if (inspection.uvMeshCount !== expectUvMeshes) {
      throw new AppError(
        'GLB_EXPORT_INVALID',
        `The texture-free export lost UV coordinates: ${expectUvMeshes} mesh(es) had a UV map ` +
          `and only ${inspection.uvMeshCount} of ${inspection.meshCount} came back with one. ` +
          'Textures could never be re-attached to this file, so it was not written.',
        {
          expectUvMeshes,
          uvMeshCount: inspection.uvMeshCount,
          meshCount: inspection.meshCount,
        },
      );
    }
  }

  const exported = inspection.clips.map((c) => c.name);
  const selected = clips.map((c) => c.name);
  const { missing: notExported, unexpected } = diffClipNames(exported, selected);

  if (notExported.length > 0 || unexpected.length > 0) {
    const detail = [
      notExported.length > 0 ? `missing ${notExported.map((n) => `"${n}"`).join(', ')}` : '',
      unexpected.length > 0 ? `unexpected ${unexpected.map((n) => `"${n}"`).join(', ')}` : '',
    ]
      .filter(Boolean)
      .join('; ');
    throw new AppError(
      'GLB_EXPORT_INVALID',
      `The exported GLB does not contain the selected animations (${detail}). ` +
        `Exported ${exported.length}, selected ${selected.length}.`,
      { exported, selected, missing: notExported, unexpected },
    );
  }
}
